// The logo check in the browser: the same engine as the document service (@xr/logo) on the picked
// file's pixels, so the person sees the result before saving (spec 03 R-09). The service checks the
// file again on upload and keeps the fixed copy. Same as apps/mobile/src/app/logo.ts.
import { analyse, checkFile, workSize, type LogoError, type LogoResult } from '@xr/logo';

export type CheckedLogo = LogoResult & { src: string; before: string; file: File };

function decode(file: File): Promise<{ img: HTMLImageElement; url: string }> {
  return new Promise((ok, bad) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => ok({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); bad(new Error('read')); };
    img.src = url;
  });
}

export async function checkLogoFile(file: File): Promise<{ error: LogoError } | CheckedLogo> {
  const meta = { type: file.type, bytes: file.size };
  const first = checkFile(meta);
  if ('error' in first) return first;
  let img: HTMLImageElement, url: string;
  try { ({ img, url } = await decode(file)); } catch { return { error: 'read' }; }
  const nw = img.naturalWidth || 600, nh = img.naturalHeight || 600;
  const ws = workSize(nw, nh);
  const cv = document.createElement('canvas');
  cv.width = ws.width; cv.height = ws.height;
  const g = cv.getContext('2d', { willReadFrequently: true });
  if (!g) return { error: 'read' };
  g.drawImage(img, 0, 0, ws.width, ws.height);
  let data: ImageData;
  try { data = g.getImageData(0, 0, ws.width, ws.height); } catch { return { error: 'read' }; }
  const r = analyse({ data: data.data, width: ws.width, height: ws.height }, { ...meta, naturalWidth: nw, naturalHeight: nh });
  if ('error' in r) return r;
  const out = document.createElement('canvas');
  out.width = r.fixed.width; out.height = r.fixed.height;
  out.getContext('2d')!.putImageData(new ImageData(Uint8ClampedArray.from(r.fixed.data), r.fixed.width, r.fixed.height), 0, 0);
  return { ...r, src: out.toDataURL('image/png'), before: url, file };
}
